/** Did every bill arrive? Sources down the side, months across, one cell per expected period, in the
 * census's six states.
 *
 * **The six states are never collapsed, and colour is never the only carrier**: each has its own
 * colour AND its own pattern (solid, dash, hatch, dots, stripes, dashed outline) and a word in the
 * legend. A gap is not a zero: Missing and Cannot tell are not Received. The states are the product's
 * (`sources/census.py`): `submitted` is `received` here, `pending` is "Not due yet".
 *
 * It is a real `role="grid"` table - row and column headers, one roving tab stop, arrow keys, Home
 * and End - so the grid *is* the accessible alternative; a second hidden table would only read it
 * twice. Hover or focus shows a tooltip with the source, the month and what the state means. The
 * cells pop in once, staggered, in CSS (skipped under reduced motion). Cells open something only when
 * `onCellOpen` is given; without it they are focusable for the tooltip and say nothing is behind them.
 */

import { useRef, useState } from "react";
import type { KeyboardEvent } from "react";

import chart from "./Chart.module.css";
import { ChartTooltip } from "./ChartTooltip";
import { anchorWithin } from "./tipAnchor";
import type { TipAnchor } from "./tipAnchor";
import { COVERAGE_LABEL, COVERAGE_MEANING, COVERAGE_ORDER } from "./coverage";
import type { CoverageState } from "./coverage";
import styles from "./CoverageHeatmap.module.css";

export interface CoverageColumn {
  id: string;
  /** Short heading: `J`, `Jan`. */
  label: string;
  /** For the tooltip and screen readers: `January 2025`. */
  full: string;
}

export interface CoverageCell {
  state: CoverageState;
  /** Extra words for the tooltip: a reason, a received date. */
  note?: string;
}

export interface CoverageRow {
  id: string;
  label: string;
  sublabel?: string;
  /** One per column, in column order. */
  cells: readonly CoverageCell[];
}

export function CoverageHeatmap({
  rows,
  columns,
  caption,
  onCellOpen,
  testId = "coverage-heatmap",
}: {
  rows: readonly CoverageRow[];
  columns: readonly CoverageColumn[];
  /** The grid's accessible name. */
  caption: string;
  onCellOpen?: (rowId: string, columnId: string) => void;
  testId?: string;
}) {
  const wrap = useRef<HTMLDivElement>(null);
  const cellRefs = useRef(new Map<string, HTMLTableCellElement>());
  const [focus, setFocus] = useState({ r: 0, c: 0 });
  const [tip, setTip] = useState<{ anchor: TipAnchor; title: string; body: string } | null>(null);

  const used = COVERAGE_ORDER.filter((state) => rows.some((row) => row.cells.some((cell) => cell.state === state)));
  const legend = used.length ? used : COVERAGE_ORDER;

  const describe = (row: CoverageRow, c: number) => {
    const cell = row.cells[c];
    const column = columns[c];
    if (!cell || !column) return null;
    const where = row.sublabel ? `${row.label}, ${row.sublabel}` : row.label;
    return {
      title: `${where}, ${column.full}`,
      body: `${COVERAGE_LABEL[cell.state]}. ${cell.note ?? COVERAGE_MEANING[cell.state]}`,
    };
  };

  const show = (r: number, c: number) => {
    const row = rows[r];
    const element = cellRefs.current.get(`${r}:${c}`);
    if (!row || !element || !wrap.current) return;
    const text = describe(row, c);
    if (text) setTip({ anchor: anchorWithin(wrap.current, element), ...text });
  };
  const move = (r: number, c: number) => {
    const next = {
      r: Math.max(0, Math.min(rows.length - 1, r)),
      c: Math.max(0, Math.min(columns.length - 1, c)),
    };
    setFocus(next);
    cellRefs.current.get(`${next.r}:${next.c}`)?.focus();
  };
  const onKey = (event: KeyboardEvent<HTMLTableCellElement>, r: number, c: number) => {
    switch (event.key) {
      case "ArrowRight":
        event.preventDefault();
        move(r, c + 1);
        break;
      case "ArrowLeft":
        event.preventDefault();
        move(r, c - 1);
        break;
      case "ArrowDown":
        event.preventDefault();
        move(r + 1, c);
        break;
      case "ArrowUp":
        event.preventDefault();
        move(r - 1, c);
        break;
      case "Home":
        event.preventDefault();
        move(r, 0);
        break;
      case "End":
        event.preventDefault();
        move(r, columns.length - 1);
        break;
      case "Escape":
        setTip(null);
        break;
      case "Enter":
      case " ":
        if (onCellOpen) {
          event.preventDefault();
          const row = rows[r];
          const column = columns[c];
          if (row && column) onCellOpen(row.id, column.id);
        }
        break;
      default:
    }
  };

  return (
    <div ref={wrap} className={`${chart.chart} ${styles.wrap}`} data-testid={testId}>
      <div className={styles.scroll}>
        <table className={styles.grid} role="grid" aria-label={caption}>
          <thead>
            <tr>
              <th scope="col" className={styles.corner}>
                <span className="visuallyHidden">Source</span>
              </th>
              {columns.map((column) => (
                <th key={column.id} scope="col" className={styles.colHead}>
                  <span aria-hidden="true">{column.label}</span>
                  <span className="visuallyHidden">{column.full}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, r) => (
              <tr key={row.id}>
                <th scope="row" className={styles.rowHead}>
                  <span className={styles.rowLabel}>{row.label}</span>
                  {row.sublabel ? <span className={styles.rowSub}>{row.sublabel}</span> : null}
                </th>
                {row.cells.map((cell, c) => {
                  const text = describe(row, c);
                  return (
                    <td
                      key={columns[c]?.id ?? c}
                      ref={(node) => {
                        if (node) cellRefs.current.set(`${r}:${c}`, node);
                        else cellRefs.current.delete(`${r}:${c}`);
                      }}
                      role="gridcell"
                      tabIndex={focus.r === r && focus.c === c ? 0 : -1}
                      aria-label={text ? `${text.title}: ${text.body}` : undefined}
                      className={styles.cellBox}
                      data-state={cell.state}
                      data-testid="coverage-cell"
                      onClick={onCellOpen ? () => onCellOpen(row.id, columns[c]?.id ?? "") : undefined}
                      onKeyDown={(event) => onKey(event, r, c)}
                      onFocus={() => {
                        setFocus({ r, c });
                        show(r, c);
                      }}
                      onBlur={() => setTip(null)}
                      onPointerEnter={() => show(r, c)}
                      onPointerLeave={() => setTip(null)}
                    >
                      <span
                        aria-hidden="true"
                        className={`${styles.hmCell} ${styles[`s_${cell.state}`]}`}
                        style={{ animationDelay: `${r * 40 + c * 18}ms` }}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ul className={`${chart.legend} ${styles.legend}`} data-testid="coverage-legend">
        {legend.map((state) => (
          <li key={state} className={chart.legendItem}>
            <span aria-hidden="true" className={`${styles.hmSwatch} ${styles[`s_${state}`]}`} />
            {COVERAGE_LABEL[state]}
          </li>
        ))}
      </ul>
      <ChartTooltip anchor={tip?.anchor ?? null}>
        <b>{tip?.title}</b>
        {tip?.body}
      </ChartTooltip>
    </div>
  );
}
